// src/Contexts/AlumniRosterProvider.tsx
import React, { useContext, useEffect, useState } from "react";
import { Filters } from "react-table";
import { AuthContext } from "../Auth/AuthProvider";
import { ProfileContext } from "../Auth/ProfileProvider";
import Resident from "../Residents/Models/Resident";
import { abpnClient } from "../Services/AbpnClient";
import { PostGraduateYear } from "../Training/Models/PostGraduateYear";

interface IAlumniRosterProviderValue {
    roster: Array<Resident> | null;
    filteredRoster: Array<Resident> | null;
    filters: Filters<object>;
    isRosterLoading: boolean;
    updateFilteredRoster: (filteredResidents: Array<Resident>) => void;
    updateFilters: (filters: Filters<object>) => void;
}

export const AlumniRosterContext = React.createContext<IAlumniRosterProviderValue>(null as any);

export const AlumniRosterProvider = ({ children }: { children: JSX.Element }) => {
    const [roster, setRoster] = useState<Array<Resident> | null>(null);
    const [filteredRoster, setFilteredRoster] = useState<Array<Resident> | null>(null);
    const [filters, setFilters] = useState<Filters<object>>([]);
    const [isRosterLoading, setIsRosterLoading] = useState<boolean>(false);

    const { user } = useContext(ProfileContext);
    const authContext = useContext(AuthContext);

    const updateFilters = (f: Filters<object>) => setFilters(f);
    const updateFilteredRoster = (res: Array<Resident>) => setFilteredRoster(res);

    const sortLastName = (a: Resident, b: Resident) =>
        (a.name?.lastName ?? "").localeCompare(b.name?.lastName ?? "", undefined, { sensitivity: "base" });

    // Safely get last PGY for the user's selected program
    const getProgramLastPgyAttended = (years: Array<PostGraduateYear>, tpId: number | undefined) => {
        if (!Array.isArray(years) || !tpId) return undefined;
        const programYears = years.filter(y => y.trainingProgramId === tpId);
        if (!programYears.length) return undefined;
        return [...programYears].sort(
            (a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime()
        )[programYears.length - 1];
    };

    const sortPgyDesc = (tpId: number | undefined) => (a: Resident, b: Resident) => {
        const aLast = getProgramLastPgyAttended(a.postGraduateYears, tpId);
        const bLast = getProgramLastPgyAttended(b.postGraduateYears, tpId);
        const aYear = aLast ? new Date(aLast.endDate).getFullYear() : -Infinity;
        const bYear = bLast ? new Date(bLast.endDate).getFullYear() : -Infinity;
        // Newest year first
        return bYear - aYear || sortLastName(a, b);
    };

    useEffect(() => {
        // Guard: don’t run until we have a user and a selected program
        if (!user || !user.selectedTrainingProgramId) return;

        let cancelled = false;
        const ctrl = new AbortController();

        const loadRoster = async () => {
            try {
                setIsRosterLoading(true);

                const program = user.trainingPrograms?.find(
                    (p) => p.trainingProgramId === user.selectedTrainingProgramId
                );
                if (!program) {
                    if (!cancelled) {
                        setRoster([]);
                        setFilteredRoster([]);
                        setIsRosterLoading(false);
                    }
                    return;
                }

                const url = `/api/dashboard/GetAllResidents?programTypeId=${program.trainingProgramTypeId}`;

                // pass AbortSignal so fetch can be cancelled on unmount
                const rosterResult = await abpnClient.get<Array<Resident>>(url, authContext, undefined, ctrl.signal);

                if (cancelled) return;

                const sorted = (rosterResult ?? [])
                    .filter(r => r.currentTrainingProgramId !== user.selectedTrainingProgramId)
                    .sort(sortPgyDesc(user.selectedTrainingProgramId)); // primary sort by last PGY year desc, tie-break by last name

                setRoster(sorted);
                setFilteredRoster(sorted);
            } catch (e) {
                if (cancelled) return; // ignore errors after unmount/abort
                // abpnClient already redirects on fatal errors; here we just stop the spinner
                console.warn("Failed to load alumni roster:", e);
            } finally {
                if (!cancelled) setIsRosterLoading(false);
            }
        };

        loadRoster();

        return () => {
            cancelled = true;
            ctrl.abort(); // cancels the in-flight fetch to prevent setState on unmounted component
        };
    }, [authContext, user?.selectedTrainingProgramId]); // react when the selected program changes

    return (
        <AlumniRosterContext.Provider
            value={{ roster, filteredRoster, filters, isRosterLoading, updateFilteredRoster, updateFilters }}
        >
            {children}
        </AlumniRosterContext.Provider>
    );
};
